import {
  buildPositionedWriteRequest,
  normalizeDirectGallerySession,
  validateDirectGalleryAck,
  validatePositionedWriteAck
} from './direct-gallery-protocol.js';

function asSafeError(error, fallback, secret = '') {
  const raw = error instanceof Error ? error.message : String(error || '');
  const message = secret && raw.includes(secret)
    ? fallback
    : (raw || fallback);
  return new Error(message);
}

export function createDirectGallerySink({
  session: sessionValue,
  fetchFn = globalThis.fetch,
  controller
} = {}) {
  if (typeof fetchFn !== 'function') throw new TypeError('fetchFn is required');
  if (!controller || typeof controller.finalize !== 'function') {
    throw new TypeError('Direct Gallery controller is required');
  }

  const normalized = normalizeDirectGallerySession(sessionValue);
  const sessionId = normalized.sessionId;
  const uploadUrl = normalized.uploadUrl;
  const maxQueuedBytes = normalized.maxQueuedBytes;
  let authToken = normalized.token;
  normalized.token = '';
  let nextOffset = normalized.nextOffset;
  let queuedBytes = 0;
  let pendingWrites = 0;
  let bytesAcknowledged = nextOffset;
  let chunkCount = 0;
  let lastAcknowledgedAt = 0;
  let acceptingWrites = true;
  let closed = false;
  let failure = null;
  let finishPromise = null;
  let abortNotified = false;
  let writeChain = Promise.resolve();

  function closeWithFailure(error, fallback = 'Direct Gallery sink failed') {
    if (!failure) failure = asSafeError(error, fallback, authToken);
    acceptingWrites = false;
    closed = true;
    authToken = '';
    return failure;
  }

  async function upload(blob, offset) {
    if (closed || failure) {
      throw failure || new Error('Direct Gallery sink is closed');
    }
    let response;
    try {
      response = await fetchFn(uploadUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/octet-stream',
          'X-Recording-Offset': String(offset)
        },
        body: blob,
        cache: 'no-store',
        credentials: 'omit'
      });
    } catch (error) {
      throw closeWithFailure(error, 'Direct Gallery upload failed');
    }
    if (!response?.ok) {
      throw closeWithFailure(
        new Error(`Direct Gallery upload failed (${Number(response?.status) || 0})`)
      );
    }

    let acknowledgement;
    try {
      acknowledgement = await response.json();
      nextOffset = validateDirectGalleryAck({
        expectedOffset: offset,
        chunkBytes: blob.size,
        ack: acknowledgement
      });
    } catch (error) {
      throw closeWithFailure(error, 'Direct Gallery acknowledgement failed');
    }
    bytesAcknowledged = nextOffset;
    chunkCount++;
    lastAcknowledgedAt = Date.now();
    return acknowledgement;
  }

  async function uploadAt(request, position, data) {
    if (closed || failure) {
      throw failure || new Error('Direct Gallery sink is closed');
    }

    let response;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        response = await fetchFn(request.url, {
          method: request.method,
          headers: request.headers,
          body: data,
          cache: 'no-store',
          credentials: 'omit'
        });
      } catch (error) {
        if (attempt === 0) continue;
        throw closeWithFailure(error, 'Direct Gallery positioned upload failed');
      }
      if (response?.ok) break;
      if (Number(response?.status) >= 500 && attempt === 0) continue;
      throw closeWithFailure(
        new Error(`Direct Gallery positioned upload failed (${Number(response?.status) || 0})`),
        'Direct Gallery positioned upload failed'
      );
    }

    if (!response?.ok) {
      throw closeWithFailure(
        new Error('Direct Gallery positioned upload failed'),
        'Direct Gallery positioned upload failed'
      );
    }

    let acknowledgement;
    try {
      acknowledgement = validatePositionedWriteAck({
        position,
        dataBytes: data.byteLength,
        ack: await response.json()
      });
    } catch (error) {
      throw closeWithFailure(error, 'Direct Gallery positioned acknowledgement failed');
    }
    nextOffset = acknowledgement.extentBytes;
    bytesAcknowledged = acknowledgement.extentBytes;
    chunkCount++;
    lastAcknowledgedAt = Date.now();
    return acknowledgement;
  }

  function write(blob) {
    if (!acceptingWrites || closed) {
      return Promise.reject(failure || new Error('Direct Gallery sink is closed'));
    }
    if (!(blob instanceof Blob)) {
      return Promise.reject(new TypeError('Direct Gallery chunk must be a Blob'));
    }
    if (blob.size <= 0) {
      return Promise.reject(new RangeError('Direct Gallery chunk must not be empty'));
    }
    if (queuedBytes + blob.size > maxQueuedBytes) {
      return Promise.reject(new Error('Direct Gallery queue limit exceeded'));
    }

    queuedBytes += blob.size;
    pendingWrites++;
    const operation = writeChain.then(() => upload(blob, nextOffset));
    writeChain = operation.catch(() => {});
    return operation.finally(() => {
      queuedBytes = Math.max(0, queuedBytes - blob.size);
      pendingWrites = Math.max(0, pendingWrites - 1);
    });
  }

  function writeAt(data, position) {
    if (!acceptingWrites || closed) {
      return Promise.reject(failure || new Error('Direct Gallery sink is closed'));
    }

    let request;
    try {
      request = buildPositionedWriteRequest({
        sessionId,
        uploadUrl,
        token: authToken,
        nextOffset,
        maxQueuedBytes
      }, { data, position });
    } catch (error) {
      return Promise.reject(error);
    }
    if (queuedBytes + data.byteLength > maxQueuedBytes) {
      return Promise.reject(new Error('Direct Gallery queue limit exceeded'));
    }

    queuedBytes += data.byteLength;
    pendingWrites++;
    const operation = writeChain.then(() => uploadAt(request, position, data));
    writeChain = operation.catch(() => {});
    return operation.finally(() => {
      queuedBytes = Math.max(0, queuedBytes - data.byteLength);
      pendingWrites = Math.max(0, pendingWrites - 1);
    });
  }

  async function flush() {
    await writeChain;
    if (failure) throw failure;
    return { nextOffset, bytesAcknowledged };
  }

  function finish() {
    if (finishPromise) return finishPromise;
    acceptingWrites = false;
    finishPromise = (async () => {
      await flush();
      if (closed || failure) throw failure || new Error('Direct Gallery sink is closed');

      let published;
      try {
        published = await controller.finalize(sessionId);
      } catch (error) {
        throw closeWithFailure(error, 'Direct Gallery finalization failed');
      }
      const uri = published?.uri || published?.contentUri;
      const bytes = Number(published?.bytes);
      if (published?.published !== true || !uri
          || !Number.isSafeInteger(bytes) || bytes !== bytesAcknowledged) {
        throw closeWithFailure(
          new Error('Direct Gallery publication verification failed')
        );
      }
      closed = true;
      authToken = '';
      const result = {
        uri,
        bytes,
        cleanup: async () => {}
      };
      return result;
    })();
    return finishPromise;
  }

  function abort(
    error = new Error('Direct Gallery sink aborted'),
    retainPartial = true
  ) {
    if (!closed) closeWithFailure(error, 'Direct Gallery sink aborted');
    if (!abortNotified && typeof controller.abort === 'function') {
      abortNotified = true;
      Promise.resolve(controller.abort(sessionId, retainPartial)).catch(() => {});
    }
  }

  return {
    persistent: true,
    kind: 'direct-gallery',
    write,
    writeAt,
    flush,
    finish,
    abort,
    getMetrics() {
      return {
        bytesAcknowledged,
        queuedBytes,
        pendingWrites,
        chunkCount,
        nextOffset,
        maxQueuedBytes,
        lastAcknowledgedAt,
        failed: !!failure,
        closed
      };
    }
  };
}
